import { useEffect, useMemo, useState } from 'react';
import { DateFilterType } from 'src/pages/performance/tabs/overview-tab';
import {
  useGetMartAdvertisersQuery,
  useGetMartCampaignsQuery,
  useGetMartLineItemsQuery,
  useGetMartSourcesQuery,
} from 'src/redux/admin/mart-reporting/mart-reporting.endpoints';
import { MartFilterOption } from 'src/redux/admin/mart-reporting/mart-reporting.types';
import { formatPlatformLabel } from 'src/pages/reports/utils/format-reporting-labels';
import { BetaFilterSelectSkeleton } from './beta-charts/beta-chart-skeletons';
import { MartReportFilterSelect } from './mart-report-filter-select';
import { MartReportsDateRangePicker } from './mart-reports-date-range-picker';

export interface MartReportSelection {
  source: string;
  advertisers: MartFilterOption[];
  campaigns: MartFilterOption[];
  lineItems: MartFilterOption[];
  dateFilter: DateFilterType;
  startDate: string;
  endDate: string;
}

interface MartReportsFilterBarProps {
  onSelectionChange: (selection: MartReportSelection) => void;
}

const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  return {
    startDate: formatDateLocal(start),
    endDate: formatDateLocal(end),
  };
};

const MartReportsFilterBar = ({ onSelectionChange }: MartReportsFilterBarProps) => {
  const defaults = useMemo(() => getDefaultDateRange(), []);
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedAdvertisers, setSelectedAdvertisers] = useState<MartFilterOption[]>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<MartFilterOption[]>([]);
  const [selectedLineItems, setSelectedLineItems] = useState<MartFilterOption[]>([]);
  const [dateFilter, setDateFilter] = useState<DateFilterType>('last_month');
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);

  const { data: sources = [], isLoading: sourcesLoading } = useGetMartSourcesQuery();
  const advertiserIds = selectedAdvertisers.map((advertiser) => advertiser.id);
  const campaignIds = selectedCampaigns.map((campaign) => campaign.id);

  const { data: advertisers = [], isFetching: advertisersFetching } = useGetMartAdvertisersQuery(
    { source: selectedSource },
    { skip: !selectedSource },
  );
  const { data: campaigns = [], isFetching: campaignsFetching } = useGetMartCampaignsQuery(
    { source: selectedSource, advertiser_ids: advertiserIds },
    { skip: !selectedSource },
  );
  const { data: lineItems = [], isFetching: lineItemsFetching } = useGetMartLineItemsQuery(
    {
      source: selectedSource,
      advertiser_ids: advertiserIds,
      campaign_ids: campaignIds,
    },
    { skip: !selectedSource },
  );

  useEffect(() => {
    if (sources.length > 0 && !selectedSource) {
      setSelectedSource(sources[0].id);
    }
  }, [sources, selectedSource]);

  useEffect(() => {
    onSelectionChange({
      source: selectedSource,
      advertisers: selectedAdvertisers,
      campaigns: selectedCampaigns,
      lineItems: selectedLineItems,
      dateFilter,
      startDate,
      endDate,
    });
  }, [
    selectedSource,
    selectedAdvertisers,
    selectedCampaigns,
    selectedLineItems,
    dateFilter,
    startDate,
    endDate,
    onSelectionChange,
  ]);

  const handleDateChange = (filter: DateFilterType, nextStartDate: string, nextEndDate: string) => {
    setDateFilter(filter);
    setStartDate(nextStartDate);
    setEndDate(nextEndDate);
  };

  return (
    <div className="mb-6 rounded-xl border border-[#E8E9ED] bg-white p-4 shadow-[0_1px_3px_rgba(13,15,26,0.04)]">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="min-w-0">
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.5px] text-[#8A8EA0]">
              Platform
            </label>
            {sourcesLoading ? (
              <BetaFilterSelectSkeleton />
            ) : (
              <MartReportFilterSelect
                aria-label="Platform"
                value={selectedSource}
                active={Boolean(selectedSource)}
                onChange={(value) => {
                  setSelectedSource(value);
                  setSelectedAdvertisers([]);
                  setSelectedCampaigns([]);
                  setSelectedLineItems([]);
                }}
              >
                {sources.map((source) => (
                  <option key={source.id} value={source.id}>
                    {formatPlatformLabel(source.id)}
                  </option>
                ))}
              </MartReportFilterSelect>
            )}
          </div>

          <div className="min-w-0">
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.5px] text-[#8A8EA0]">
              Advertiser
            </label>
            {sourcesLoading || (selectedSource && advertisersFetching && advertisers.length === 0) ? (
              <BetaFilterSelectSkeleton />
            ) : (
              <MartReportFilterSelect
              aria-label="Advertiser"
              value={selectedAdvertisers[0]?.id ?? ''}
              active={selectedAdvertisers.length > 0}
              onChange={(value) => {
                const advertiser = advertisers.find((item) => item.id === value);
                setSelectedAdvertisers(advertiser ? [advertiser] : []);
                setSelectedCampaigns([]);
                setSelectedLineItems([]);
              }}
            >
              <option value="">All advertisers</option>
              {advertisers.map((advertiser) => (
                <option key={advertiser.id} value={advertiser.id}>
                  {advertiser.name}
                </option>
              ))}
            </MartReportFilterSelect>
            )}
          </div>

          <div className="min-w-0">
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.5px] text-[#8A8EA0]">
              Campaign
            </label>
            {sourcesLoading || (selectedSource && campaignsFetching && campaigns.length === 0) ? (
              <BetaFilterSelectSkeleton />
            ) : (
              <MartReportFilterSelect
              aria-label="Campaign"
              value={selectedCampaigns[0]?.id ?? ''}
              active={selectedCampaigns.length > 0}
              onChange={(value) => {
                const campaign = campaigns.find((item) => item.id === value);
                setSelectedCampaigns(campaign ? [campaign] : []);
                setSelectedLineItems([]);
              }}
            >
              <option value="">All campaigns</option>
              {campaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </MartReportFilterSelect>
            )}
          </div>

          <div className="min-w-0">
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.5px] text-[#8A8EA0]">
              Line item
            </label>
            {sourcesLoading || (selectedSource && lineItemsFetching && lineItems.length === 0) ? (
              <BetaFilterSelectSkeleton />
            ) : (
              <MartReportFilterSelect
              aria-label="Line item"
              value={selectedLineItems[0]?.id ?? ''}
              active={selectedLineItems.length > 0}
              onChange={(value) => {
                const lineItem = lineItems.find((item) => item.id === value);
                setSelectedLineItems(lineItem ? [lineItem] : []);
              }}
            >
              <option value="">All line items</option>
              {lineItems.map((lineItem) => (
                <option key={lineItem.id} value={lineItem.id}>
                  {lineItem.name}
                </option>
              ))}
            </MartReportFilterSelect>
            )}
          </div>
        </div>

        <div className="shrink-0">
          <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.5px] text-[#8A8EA0]">
            Date range
          </label>
          <MartReportsDateRangePicker
            dateFilter={dateFilter}
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateChange}
          />
        </div>
      </div>
    </div>
  );
};

export default MartReportsFilterBar;
